export type EligibilityEmailFlagField = 'registerInterestEmailSent' | 'conciergeIntroEmailSent';

export type EligibilityEmailFlagRow = {
  id: string;
  registerInterestEmailSent: boolean;
  conciergeIntroEmailSent: boolean;
  finalizedApplicationCount: number;
};

export type BulkEligibilityEmailFlagUpdate = {
  id: string;
  body: {
    registerInterestEmailSent?: boolean;
    conciergeIntroEmailSent?: boolean;
  };
};

export type BulkEligibilityEmailFlagPlan = {
  field: EligibilityEmailFlagField;
  value: boolean;
  updates: Array<BulkEligibilityEmailFlagUpdate>;
  selectedCount: number;
  alreadySetCount: number;
  skippedIneligibleCount: number;
};

export const EMAIL_FLAG_FIELD_LABELS: Record<EligibilityEmailFlagField, string> = {
  registerInterestEmailSent: 'register interest email sent',
  conciergeIntroEmailSent: 'concierge intro sent',
};

export function getChangedEligibilityEmailFlags(
  current: EligibilityEmailFlagRow,
  next: Pick<EligibilityEmailFlagRow, EligibilityEmailFlagField>,
): Partial<Pick<EligibilityEmailFlagRow, EligibilityEmailFlagField>> {
  const canUpdateConcierge = current.finalizedApplicationCount > 0;
  const currentRegisterInterestEmailSent = !!current.registerInterestEmailSent;
  const currentConciergeIntroEmailSent = !!current.conciergeIntroEmailSent;

  return {
    ...(next.registerInterestEmailSent !== currentRegisterInterestEmailSent
      ? { registerInterestEmailSent: next.registerInterestEmailSent }
      : {}),
    ...(canUpdateConcierge && next.conciergeIntroEmailSent !== currentConciergeIntroEmailSent
      ? { conciergeIntroEmailSent: next.conciergeIntroEmailSent }
      : {}),
  };
}

export function getSelectedRowsById<T extends { id: string }>(
  rows: Array<T>,
  selectedIds: ReadonlyArray<string | number>,
): Array<T> {
  const ids = new Set(selectedIds.map(String));
  return rows.filter((row) => ids.has(row.id));
}

export function planBulkEligibilityEmailFlagUpdate(
  rows: Array<EligibilityEmailFlagRow>,
  field: EligibilityEmailFlagField,
  value: boolean,
): BulkEligibilityEmailFlagPlan {
  const eligibleRows =
    field === 'conciergeIntroEmailSent' ? rows.filter((row) => row.finalizedApplicationCount > 0) : rows;
  const skippedIneligibleCount = rows.length - eligibleRows.length;
  const updates = eligibleRows
    .filter((row) => row[field] !== value)
    .map((row) => ({ id: row.id, body: { [field]: value } }));

  return {
    field,
    value,
    updates,
    selectedCount: rows.length,
    alreadySetCount: eligibleRows.length - updates.length,
    skippedIneligibleCount,
  };
}

export function getBulkEligibilityEmailFlagConfirmMessage(plan: BulkEligibilityEmailFlagPlan): string {
  const flagLabel = EMAIL_FLAG_FIELD_LABELS[plan.field];
  const userLabel = plan.updates.length === 1 ? 'eligible user' : 'eligible users';
  const parts = [`This will mark ${flagLabel} for ${plan.updates.length} ${userLabel} on this page.`];

  if (plan.alreadySetCount > 0) {
    parts.push(
      `${plan.alreadySetCount} selected ${plan.alreadySetCount === 1 ? 'user is' : 'users are'} already marked.`,
    );
  }

  if (plan.skippedIneligibleCount > 0) {
    parts.push(
      `${plan.skippedIneligibleCount} selected ${
        plan.skippedIneligibleCount === 1 ? 'user has' : 'users have'
      } no finalized application and will be skipped.`,
    );
  }

  return parts.join(' ');
}

export function getBulkEligibilityEmailFlagResultMessage({
  updatedCount,
  failedCount,
  skippedIneligibleCount,
}: {
  updatedCount: number;
  failedCount: number;
  skippedIneligibleCount: number;
}): string {
  const parts = [`Updated ${updatedCount} ${updatedCount === 1 ? 'eligible user' : 'eligible users'}.`];

  if (failedCount > 0) {
    parts.push(`${failedCount} ${failedCount === 1 ? 'update' : 'updates'} failed.`);
  }

  if (skippedIneligibleCount > 0) {
    parts.push(
      `${skippedIneligibleCount} selected ${
        skippedIneligibleCount === 1 ? 'user was' : 'users were'
      } skipped because they have no finalized application.`,
    );
  }

  return parts.join(' ');
}

export function getBulkEligibilityEmailFlagEmptyMessage(plan: BulkEligibilityEmailFlagPlan): string {
  if (plan.skippedIneligibleCount > 0 && plan.alreadySetCount === 0) {
    return 'Concierge intro can only be updated for users with a finalized application.';
  }

  return 'Selected users are already marked.';
}
